import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus, BarChart3, Upload } from "lucide-react";
import { Btn } from "../../components/Btn";
import { Modal } from "../../components/Modal";
import { useToast } from "../../components/Toast";
import { Sparkline } from "../../components/Sparkline";
import { StatusChip } from "../../components/dashboardModals";
import { createApp, getMyApps } from "../../api/endpoints";
import { useAuth } from "../../context/AuthContext";
import { ApiError, type DevAppOut } from "../../api/types";

export default function MyAppsPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [createOpen, setCreateOpen] = useState(false);

  const myAppsQ = useQuery({
    queryKey: ["dev", "my-apps"],
    queryFn: getMyApps,
  });

  const apps: DevAppOut[] = myAppsQ.data ?? [];
  const loading = myAppsQ.isLoading;

  return (
    <>
      <header className="flex flex-col gap-3 pb-5 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
        <div className="min-w-0">
          <h1 className="text-[20px] font-semibold leading-tight tracking-[-0.01em] text-[var(--color-fg)] sm:text-[22px]">
            Мои приложения
          </h1>
          <div className="mt-1 text-[12.5px] text-[var(--color-fg-muted)]">
            <span className="font-mono">{user?.login ?? "—"}</span> · {apps.length} приложений · видны только вам
          </div>
        </div>
        <Btn icon={Plus} variant="primary" onClick={() => setCreateOpen(true)}>Новое приложение</Btn>
      </header>

      <div className={"grid grid-cols-1 gap-3 md:grid-cols-2 " + (loading ? "opacity-70" : "")}>
        {apps.map((a, i) => (
          <AppCard key={a.pkg} app={a} index={i} onOpen={() => navigate(`/admin/apps/${encodeURIComponent(a.pkg)}`)} />
        ))}
        {!loading && !apps.length && (
          <div className="grid place-items-center rounded-xl border border-dashed border-[var(--color-border-strong)] bg-[var(--color-surface)] px-6 py-16 text-center md:col-span-2">
            <div className="text-[14px] font-medium text-[var(--color-fg)]">У вас пока нет приложений</div>
            <div className="mt-1 text-[12.5px] text-[var(--color-fg-muted)]">Создайте первое — нажмите «Новое приложение».</div>
          </div>
        )}
        {loading && !apps.length && (
          <div className="grid place-items-center rounded-xl border border-dashed border-[var(--color-border-strong)] bg-[var(--color-surface)] px-6 py-16 text-center md:col-span-2">
            <div className="text-[12.5px] text-[var(--color-fg-muted)]">Загрузка…</div>
          </div>
        )}
      </div>
      <CreateDevAppModal open={createOpen} onClose={() => setCreateOpen(false)} />
    </>
  );
}

function CreateDevAppModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const qc = useQueryClient();
  const push = useToast();
  const [pkg, setPkg] = useState("");
  const [name, setName] = useState("");
  const createMut = useMutation({
    mutationFn: () => createApp({
      pkg: pkg.trim(),
      name: name.trim(),
      visibility: "ALL",
      icon_letter: name.trim().slice(0, 1) || "A",
    }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ["dev", "my-apps"] });
      push({ kind: "success", text: "Приложение создано" });
      setPkg("");
      setName("");
      onClose();
    },
    onError: (error) => push({
      kind: "warn",
      text: error instanceof ApiError ? error.message : "Не удалось создать приложение",
    }),
  });
  const canSubmit = /^[a-zA-Z0-9._-]{2,}$/.test(pkg.trim()) && name.trim().length >= 2;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Новое приложение"
      subtitle="Создаст черновик в вашем кабинете"
      size="sm"
      footer={
        <>
          <Btn onClick={onClose}>Отмена</Btn>
          <Btn
            variant="primary"
            disabled={!canSubmit || createMut.isPending}
            onClick={() => createMut.mutate()}
          >
            {createMut.isPending ? "Создание…" : "Создать"}
          </Btn>
        </>
      }
    >
      <div className="space-y-3">
        <label className="block text-[12px] text-[var(--color-fg)]">
          Package name
          <input
            value={pkg}
            onChange={(event) => setPkg(event.target.value.replace(/\s+/g, ""))}
            placeholder="ru.company.app"
            className="mt-1 w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] px-3 py-2 font-mono text-[13px] outline-none focus:border-[var(--color-fg-muted)]"
          />
        </label>
        <label className="block text-[12px] text-[var(--color-fg)]">
          Название
          <input
            value={name}
            onChange={(event) => setName(event.target.value)}
            className="mt-1 w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] px-3 py-2 text-[13px] outline-none focus:border-[var(--color-fg-muted)]"
          />
        </label>
      </div>
    </Modal>
  );
}

function AppCard({
  app,
  index,
  onOpen,
}: {
  app: DevAppOut;
  index: number;
  onOpen: () => void;
}) {
  const navigate = useNavigate();
  // Гистограмма-плейсхолдер — на /dev/my-apps бэк не отдаёт точки 30 дней,
  // поэтому показываем сглаженный паттерн от downloads_30d/installs.
  const values = useMemo(
    () => Array.from({ length: 30 }, (_, i) => 60 + Math.round(Math.sin((i + index) / 3) * 30) + i * 4),
    [index],
  );
  const isRevoked = app.status === "revoked";

  return (
    <section
      role="button"
      tabIndex={0}
      onClick={onOpen}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onOpen();
        }
      }}
      style={{ animationDelay: `${index * 50}ms` }}
      className="stagger-up cursor-pointer rounded-2xl border border-[var(--color-border)] bg-[var(--color-surface)] p-4 text-left transition-all hover:-translate-y-0.5 hover:shadow-[0_10px_28px_-14px_rgba(15,23,42,0.16)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-brand)]"
    >
      <div className="flex items-start gap-3">
        <span
          className="grid shrink-0 place-items-center rounded-2xl font-semibold text-white"
          style={{ background: app.icon_color, width: 48, height: 48, fontSize: 22 }}
        >
          {app.icon_letter}
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <div className="truncate text-[15px] font-semibold text-[var(--color-fg)]">{app.name}</div>
              <div className="truncate font-mono text-[11.5px] text-[var(--color-fg-muted)]">{app.pkg}</div>
            </div>
            {isRevoked ? (
              <span className="shrink-0 rounded-full bg-[var(--color-danger-bg)] px-2 py-0.5 text-[10.5px] font-medium text-[var(--color-danger)]">
                Отозвано
              </span>
            ) : (
              <StatusChip status={app.status as "published" | "revoked" | "draft"} />
            )}
          </div>
          <div className="mt-2 text-[12px] text-[var(--color-fg-muted)]">
            {app.status === "published" && <>Опубликовано · поколение {app.current_gen ?? "—"}</>}
            {app.status === "draft"     && <>Черновик · ожидает публикации</>}
            {app.status === "revoked"   && <>Отозвано</>}
          </div>
        </div>
      </div>

      <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-[1fr_1fr_auto]">
        <div>
          <div className="text-[10.5px] font-medium uppercase tracking-[0.08em] text-[var(--color-fg-muted)]">Установки</div>
          <div className="mt-0.5 text-[18px] font-semibold tracking-[-0.01em] text-[var(--color-fg)]">{app.installs.toLocaleString("ru-RU")}</div>
        </div>
        <div>
          <div className="text-[10.5px] font-medium uppercase tracking-[0.08em] text-[var(--color-fg-muted)]">Скачивания</div>
          <div className="mt-0.5 text-[18px] font-semibold tracking-[-0.01em] text-[var(--color-fg)]">{app.downloads_30d.toLocaleString("ru-RU")}</div>
        </div>
        <div className="col-span-2 sm:col-span-1">
          <div className="text-[10.5px] font-medium uppercase tracking-[0.08em] text-[var(--color-fg-muted)]">30 дней</div>
          <Sparkline
            values={values}
            color={isRevoked ? "var(--color-danger)" : "var(--color-brand-2)"}
            className="mt-1 h-8 w-full sm:w-[120px]"
          />
        </div>
      </div>

      <div
        className="mt-4 flex flex-wrap items-center justify-between gap-2 border-t border-[var(--color-border)] pt-3"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex gap-2">
          <Btn icon={Upload} size="sm" onClick={() => navigate(`/dev/apps/${encodeURIComponent(app.pkg)}/upload`)}>
            Новая версия
          </Btn>
          <Btn icon={BarChart3} size="sm" variant="ghost" onClick={() => navigate(`/dev/stats?pkg=${encodeURIComponent(app.pkg)}`)}>
            Статистика
          </Btn>
        </div>
        <span className="font-mono text-[11px] text-[var(--color-fg-muted)]">v {app.current_version ?? "—"}</span>
      </div>
    </section>
  );
}
